export const createQueryString = (
  params: Record<string, string | number | boolean | string[] | number[]>
) => {
  return Object.entries(params)
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map((v) => `${key}=${encodeURIComponent(v)}`) // Handle arrays correctly
        : `${key}=${encodeURIComponent(value)}`
    )
    .join("&");
};

export function generatePassword(length = 12) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+[]{}|;:,.<>?";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

// Helper function to generate date ranges for "Today", "Yesterday", etc.
export const getPredefinedRange = (range: string) => {
  const today = new Date();

  switch (range) {
    case "Today": {
      const startDate = today;
      const endDate = today;
      return { startDate, endDate };
    }

    case "Yesterday": {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      const startDate = yesterday;
      const endDate = yesterday;
      return { startDate, endDate };
    }

    case "Last 7 days": {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      const startDate = sevenDaysAgo;
      const endDate = today;
      return { startDate, endDate };
    }

    case "Last 30 days": {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      const startDate = thirtyDaysAgo;
      const endDate = today;
      return { startDate, endDate };
    }

    case "This Month": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startDate = firstOfMonth;
      const endDate = today;
      return { startDate, endDate };
    }

    case "Last Month": {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const startDate = lastMonth;
      const endDate = lastMonthEnd;
      return { startDate, endDate };
    }

    default:
      return { startDate: null, endDate: null };
  }
};

  // Function to format time from "19/11/2025, 19:47:50" to "07:47 PM"
 export  const formatTimeToAMPM = (timeString: string): string => {
    if (!timeString) return timeString;
    
    try {
      // Parse the time string "19/11/2025, 19:47:50"
      const timeMatch = timeString.match(/(\d{1,2}):(\d{2}):(\d{2})/);
      if (!timeMatch) return timeString;
      
      const hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12; // Convert to 12-hour format
      const formattedHours = displayHours.toString().padStart(2, '0');
      
      return `${formattedHours}:${minutes} ${period}`;
    } catch (error) {
      // If parsing fails, return original string
      return timeString;
    }
  };

  // Function to extract date from time string for comparison
 export  const getDateFromTimeString = (timeString: string): string | null => {
    if (!timeString) return null;
    
    try {
      // Extract date part from "19/11/2025, 19:47:50"
      const dateMatch = timeString.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      return dateMatch ? dateMatch[1] : null;
    } catch (error) {
      return null;
    }
  };

  // Function to format date from "19/11/2025, 19:47:50" to "19 NOV"
//  export  const formatDateToDDMMM = (timeString: string): string => {
//     if (!timeString) return "";
//     console.log("timeString", timeString);
    
//     try {
//       // Extract date part from "19/11/2025, 19:47:50"
//       const dateMatch = timeString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
//         console.warn(dateMatch);
//       if (!dateMatch) return "";
      
//       const day = parseInt(dateMatch[1], 10);
//       const month = parseInt(dateMatch[2], 10);
      
//       // Month names array
//       const monthNames = [
//         "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
//         "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
//       ];
      
//       if (month >= 1 && month <= 12) {
//         return `${day} ${monthNames[month - 1]}`;
//       }
      
//       return "";
//     } catch (error) {
//       return "";
//     }
//   };

export const formatDateToDDMMM = (timeString: string): string => {
  if (!timeString) return "";
  
  try {
    // Extract date parts from "11/20/2025, 9:31:04 PM"
    const dateMatch = timeString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!dateMatch) return "";
    
    const first = parseInt(dateMatch[1], 10);
    const second = parseInt(dateMatch[2], 10);
    const year = parseInt(dateMatch[3], 10);
    
    let day: number, month: number;
    
    // Determine format based on which number is > 12
    if (first > 12) {
      // Must be DD/MM/YYYY
      day = first;
      month = second;
    } else if (second > 12) {
      // Must be MM/DD/YYYY
      month = first;
      day = second;
    } else {
      // Ambiguous case - try parsing and see what works
      // Try MM/DD/YYYY first (more common in web contexts)
      const testDate1 = new Date(year, first - 1, second);
      const testDate2 = new Date(year, second - 1, first);
      
      // Use whichever creates a valid date that matches the input
      if (testDate1.getMonth() === first - 1 && testDate1.getDate() === second) {
        month = first;
        day = second;
      } else if (testDate2.getMonth() === second - 1 && testDate2.getDate() === first) {
        day = first;
        month = second;
      } else {
        // Default to MM/DD/YYYY if both are valid
        month = first;
        day = second;
      }
    }
    
    const monthNames = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
    ];
    
    // Validate
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      console.warn(`Invalid date: ${timeString}`);
      return "";
    }
    
    return `${day} ${monthNames[month - 1]}`;
  } catch (error) {
    console.error("Date formatting error:", error);
    return "";
  }
};

